import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LangProvider";
import { HISTOIRE_IMAGE, histoireContent } from "../i18n/translations";

const Histoire = () => {
  const { lang } = useLang();
  const c = histoireContent[lang];

  return (
    <section
      id="histoire"
      data-testid="histoire-section"
      className="bg-surface py-24 md:py-32 relative grain overflow-hidden"
    >
      {/* Decorative gold corner frames */}
      <div className="absolute top-8 left-8 w-20 h-20 border-t border-l border-copper/40 pointer-events-none" />
      <div className="absolute bottom-8 right-8 w-20 h-20 border-b border-r border-copper/40 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="flex items-center gap-3 justify-center mb-6">
            <span className="divider-line" />
            <span className="overline">{c.overline}</span>
            <span className="divider-line" />
          </div>
          <h2
            data-testid="histoire-title"
            className="font-serif font-light text-ivory text-4xl sm:text-5xl lg:text-7xl tracking-tight"
          >
            <span className="italic text-copper">{c.title}</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-ivory/65 text-base sm:text-lg leading-relaxed font-serif italic">
            {c.intro}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Timeline left */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative"
          >
            {/* Vertical line */}
            <div className="absolute left-[7px] top-3 bottom-3 w-px bg-gradient-to-b from-copper via-copper/40 to-transparent hidden sm:block" />

            <div className="space-y-12">
              {c.timeline.map((item, i) => (
                <motion.div
                  key={i}
                  data-testid={`histoire-step-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.7, delay: i * 0.12 }}
                  className="relative pl-0 sm:pl-12"
                >
                  {/* Dot */}
                  <div className="hidden sm:block absolute left-0 top-2 w-4 h-4 border border-copper bg-obsidian">
                    <div className="absolute inset-1 bg-copper" />
                  </div>

                  <div className="overline mb-3 text-copper">{item.year}</div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-ivory leading-tight mb-3">
                    {item.title}
                  </h3>
                  <p className="text-ivory/70 text-base sm:text-[17px] leading-relaxed">
                    {item.text}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Welcome line */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-14 pt-10 border-t border-edge sm:pl-12"
            >
              <p className="font-serif text-3xl sm:text-4xl text-ivory leading-snug">
                {c.welcome}
              </p>
              <p className="mt-2 font-serif italic text-2xl sm:text-3xl text-copper">
                {c.bonAppetit}
              </p>
            </motion.div>
          </motion.div>

          {/* Image right */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="relative">
              <div className="relative aspect-[10/7] overflow-hidden bg-obsidian border border-edge">
                <img
                  src={HISTOIRE_IMAGE}
                  alt="Le Grizzly à travers les années — photos d'époque, articles de presse, José Rodrigues"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>

              {/* Caption */}
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="overline">Archives</span>
                <span className="text-xs text-ivory/40 font-serif italic">
                  1970 — Aujourd'hui
                </span>
              </div>

              {/* Stat card */}
              <div className="hidden lg:flex absolute -bottom-8 -left-8 flex-col items-start bg-obsidian border border-copper px-6 py-5">
                <span className="font-serif text-5xl font-light text-copper leading-none">
                  55+
                </span>
                <span className="mt-2 overline">
                  {lang === "fr" ? "Années de tradition" : "Years of tradition"}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Histoire;
